<script lang="ts" setup>
import WidgetLayout from '~/components/Widgets/WidgetLayout.vue'
import type { TranslatableString } from '~/types/widgets/widgetTypes'

interface Props {
  category: {
    name: TranslatableString
    storeLogos: {
      logo: TranslatableString
      url: string
    }[]
  }[]
  backgroundColor?: string
}

const props = defineProps<Props>()
const active = ref(0)
const { t } = useLang()
</script>

<template>
  <WidgetLayout :background-color="props.backgroundColor" class-name="!pb-4">
    <ClientOnly>
      <div class="brands-grid m-auto max-w-2xlg my-12">
        <div class="text-center p-4">
          <h2 class="text-[1.75rem]">{{ t('quotes.popular-loved-brands') }}</h2>
          <span> {{ t('quotes.choose-from-brands') }} </span>
        </div>
        <div></div>
        <UTabs
          v-model="active"
          :items="props.category"
          class="w-full"
          :ui="{
            wrapper: 'tabs',
            base: 'tab tab-body',
            container: '!mt-0',
            list: {
              height: 'h-auto',
              tab: {
                base: 'tab-title',
                active: '-active',
              },
            },
          }">
          <template #default="{ item: store, index }">
            <div :key="`${store.name}-${index}`" :for="String(index)">
              <span class="flex gap-1 items-center font-bold">
                {{ getWidgetTextTranslation(store.name) }}
              </span>
            </div>
          </template>
          <template #item="{ item: store, index }">
            <hr />

            <div :key="index" :name="String(index)">
              <div class="flex flex-row flex-wrap gap-4 justify-center">
                <div v-for="({ logo, url }, index) in store.storeLogos" :key="index" class="brand w-[120px] h-[120px]">
                  <a v-if="isValidUrl(url)" :href="url" target="_blank" class="logo flex p-4">
                    <img
                      v-if="getWidgetTranslatedMediaUrl(logo)"
                      :src="getWidgetTranslatedMediaUrl(logo)"
                      height="50px" />
                  </a>
                </div>
              </div>
            </div>
          </template>
        </UTabs>
      </div>
    </ClientOnly>
  </WidgetLayout>
</template>

<style lang="postcss" scoped>
.brands-grid {
  .brand {
    @apply flex items-center justify-center bg-neutral7 border border-neutral5 shadow-md rounded-md;

    a {
      @apply h-full;
    }

    img {
      @apply mix-blend-multiply object-contain;
    }

    &:hover {
      @apply bg-neutral8 dark:bg-dmGray1;
    }
  }

  :deep(.tabs) {
    padding: 0;
    color: theme('colors.text1');
    background: transparent;

    ::-webkit-scrollbar {
      height: 2px;
      background: theme('colors.quoteYellow');
    }

    .tab {
      border: 0;
      border-radius: 0;
      background: transparent;

      .tab-body {
        border: 0;
        padding: 0;
      }
    }

    .tab-title {
      display: flex;
      align-items: center;
      border-radius: theme('vars.borderRadiusHeavy');
      font-size: 0.875rem;
      background-color: theme('colors.white');
      padding: 0.75rem 1rem;
      min-height: 52px;
      color: theme('colors.brand.primary');
      border-width: 1px;
      border-color: theme('colors.brand.primary');

      &.-active {
        background-color: theme('colors.brand.primaryLight');
        border-color: transparent;
      }

      &:hover,
      &:focus {
        background-color: theme('colors.brand.primaryLightHover');
      }
    }
  }
}
</style>
